import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

interface Props {
  title: ReactNode;
  icon?: ReactNode;
  defaultOpen?: boolean;
  /**
   * Öffnet den Bereich von selbst, sobald darin etwas steht, das der Gast jetzt sehen muss —
   * etwa ein laufender Bezahlvorgang. Danach darf er wieder zumachen; erzwungen wird nur der
   * Übergang, nicht der Zustand.
   */
  openWhen?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Ein Aufklapper, dem man ansieht, dass er einer ist.
 *
 * Vorher war er eine Zeile mit einer Trennlinie darunter und einem kleinen Pfeil rechts — auf dem
 * Telefon nicht von einer Überschrift zu unterscheiden. Jetzt ist er eine Karte mit Rand und
 * eigenem Grund, der Pfeil sitzt in einem runden Feld, und die Trefferfläche ist so hoch, dass ein
 * Daumen sie sicher trifft. Offen bekommt er die Akzentfarbe am Rand: so sieht man beim Scrollen,
 * welcher Bereich gerade aufgeschlagen ist.
 */
export function Collapsible({
  title,
  icon,
  defaultOpen = false,
  openWhen = false,
  children,
  className,
}: Props) {
  const [open, setOpen] = useState(defaultOpen || openWhen);
  const panelId = useId();
  // Nur beim Übergang: sonst risse ein erneutes Rendern dem Gast den Bereich wieder auf, den er
  // gerade zugeklappt hat.
  const warOffen = useRef(openWhen);
  useEffect(() => {
    if (openWhen && !warOffen.current) {
      setOpen(true);
    }
    warOffen.current = openWhen;
  }, [openWhen]);
  return (
    <div
      className={cn(
        'bg-bg-elev rounded-guest-card shadow-guest-card overflow-hidden border transition-colors',
        open ? 'border-accent/40' : 'border-line',
        className,
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className="hover:bg-forest-soft/40 min-h-15 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors"
      >
        {icon && (
          <span className="bg-bg-soft text-fg-dim rounded-guest-cell flex size-9 shrink-0 items-center justify-center">
            {icon}
          </span>
        )}
        <span className="text-fg flex-1 font-sans text-[15px] font-bold">{title}</span>
        <span
          aria-hidden
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full border transition',
            open ? 'border-accent bg-accent text-white' : 'border-line text-fg-dim',
          )}
        >
          <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      <div id={panelId} hidden={!open} className="text-fg-dim px-4 pb-5 font-sans text-sm">
        {children}
      </div>
    </div>
  );
}
